import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { REPORT_AREAS, ReportArea } from '@optiflow/types';
import { reportAreaVisible } from '@optiflow/shared';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/utils/cn';

/**
 * The seven report areas. Overview and Sales are live; the others are shown, disabled, only when
 * the user could see at least one panel inside them. Visibility is convenience — every panel is
 * authorised again on the server.
 */
/** The areas that have a page behind them; everything else renders as a disabled label. */
const LIVE: Partial<Record<ReportArea, string>> = {
  [ReportArea.OVERVIEW]: '/reports',
  [ReportArea.SALES]: '/reports/sales',
  [ReportArea.INVENTORY]: '/reports/inventory',
  [ReportArea.FINANCE]: '/reports/finance',
  [ReportArea.CUSTOMERS]: '/reports/customers',
  [ReportArea.OPTICAL]: '/reports/optical',
  [ReportArea.CRM]: '/reports/crm',
};

export function ReportTabs() {
  const { t } = useTranslation();
  const grants = useAuthStore((state) => state.user?.permissions);
  const areas = REPORT_AREAS.filter((area) => reportAreaVisible(grants, area));

  return (
    <nav aria-label={t('reports.tabs.label')} className="-mx-1 overflow-x-auto">
      <ul className="border-line flex min-w-max gap-1 border-b px-1">
        {areas.map((area) => {
          const to = LIVE[area];
          const label = t(`reports.tabs.${area}`);
          const shape =
            'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm whitespace-nowrap';

          return (
            <li key={area}>
              {to ? (
                <NavLink
                  to={to}
                  end
                  className={({ isActive }) =>
                    cn(
                      shape,
                      isActive
                        ? 'border-brand-600 text-brand-700 font-semibold'
                        : 'text-muted hover:text-ink border-transparent',
                    )
                  }
                >
                  {label}
                </NavLink>
              ) : (
                <span aria-disabled className={cn(shape, 'text-muted border-transparent')}>
                  {label}
                  <span className="bg-canvas text-muted rounded px-1.5 py-0.5 text-[10px] font-medium">
                    {t('common.comingSoon')}
                  </span>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
